/**
 * CostDisclosure — the structural slot where cost is disclosed to whoever is about
 * to consent to something billable.
 *
 * decision-2026-07-25-remove-fabricated-trial-cost:
 *   This component renders no amount, no currency symbol, and none of the
 *   reassurance words that imply a price of zero. A zero is not a neutral
 *   placeholder — it reads as a commitment and gets quoted back. Where a price would
 *   go, we state the BASIS on which cost will be determined, and say plainly that
 *   billing is not enabled yet and that this will change.
 *
 *   `costLines` is the slot the eventual rate model fills. It is currently always
 *   passed `[]`; no caller in this codebase may pass a fabricated line. When
 *   epic-subscription-billing produces a real rate model, it fills this prop and
 *   nothing else here changes.
 *
 * Reusable by design — the meta-broker catalogue is the next consumer. Wiring it
 * there is explicitly out of scope for this work package.
 *
 * The disclosure carries a VERSION. What was disclosed is persisted alongside the
 * consent record, so a later rate change stays distinguishable from what was
 * originally agreed. Bump DISCLOSURE_VERSION whenever the wording below changes in
 * substance, never merely on a restyle.
 */

import type { CSSProperties } from 'react';
import PricingLabel from './PricingLabel';
import type { Plan } from '../hooks/usePlans';

/** Bump on any substantive change to the disclosed basis or the not-yet-enabled line. */
export const DISCLOSURE_VERSION = 'sandbox-cost-disclosure-v1';

/**
 * A single disclosed cost line. Deliberately carries no numeric field at all: a
 * caller either has a real, sourced basis to state or passes nothing.
 */
export interface CostLine {
  /** What is metered, e.g. "sandbox runtime hours". */
  label: string;
  /** How it is measured. Never a price. */
  basis: string;
}

/** The basis statements disclosed with every sandbox consent, in order. */
export const DISCLOSED_BASIS: string[] = [
  'Sandbox usage is metered on the ADR-020 usage-event path — the sandbox reports what it uses.',
  'A sandbox is reaped only after notification, and an extension can be requested without limit.',
  'Promotion out of the sandbox is a separate step and is consented to separately.',
];

/** The not-yet-enabled statement. No figure, no currency symbol, no zero. */
export const BILLING_NOT_ENABLED =
  'Billing is not yet enabled — charges do not apply to sandboxes today and this will change; ' +
  'you will be shown rates before any charge applies.';

/**
 * The exact payload persisted with a consent record so the disclosure is auditable.
 * A bare `consented: true` is not sufficient — consent has to attach to something
 * stated, and the statement has to be recoverable later.
 */
export interface DisclosedCostBasis {
  version: string;
  basis: string[];
  billing_enabled: false;
  notice: string;
  cost_lines: CostLine[];
}

export function disclosedCostBasis(costLines: CostLine[] = []): DisclosedCostBasis {
  return {
    version: DISCLOSURE_VERSION,
    basis: DISCLOSED_BASIS,
    billing_enabled: false,
    notice: BILLING_NOT_ENABLED,
    cost_lines: costLines,
  };
}

const boxStyle: CSSProperties = {
  border: '1px solid var(--border-default)',
  background: 'var(--bg-recessed)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--sp-4)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-body-sm)',
  color: 'var(--fg-2)',
};

interface Props {
  /**
   * Real, sourced cost lines. Currently always `[]` — there is no rate model. An
   * empty array renders the not-yet-enabled notice, NOT an empty box.
   */
  costLines?: CostLine[];
  /**
   * The plan being ordered, when this disclosure sits in front of an order — the
   * vertical-broker Order flow (wp-order-surface-vertical-broker). Its price is
   * disclosed through PricingLabel, which follows SL-R-009 exactly: a paid plan
   * (Professional) shows its real rate; a Trial shows "Trial · N days" —
   * time-bounded, and NEVER a fabricated zero cost
   * (decision-2026-07-25-remove-fabricated-trial-cost). Omitted for the sandbox
   * consent, where there is no plan and the not-yet-enabled notice stands alone.
   */
  plan?: Plan | null;
}

export default function CostDisclosure({ costLines = [], plan = null }: Props) {
  // The plan is priced through the SAME rules as the catalogue (PricingLabel): a
  // trial resolves to a time-bounded badge, a paid plan to its recorded rate, and
  // a zero-with-no-trial to nothing — so no fabricated figure can appear here.
  const planCurrency = plan ? Object.keys(plan.pricing)[0] ?? null : null;
  const planPricing = plan && planCurrency ? plan.pricing[planCurrency] : null;
  return (
    <section style={boxStyle} aria-labelledby="dp-cost-disclosure-heading" data-testid="cost-disclosure">
      <p className="dp-eyebrow" id="dp-cost-disclosure-heading" style={{ margin: 0 }}>
        What this costs
      </p>

      {plan && (
        <div
          data-testid="cost-disclosure-plan"
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)', marginTop: 'var(--sp-3)' }}
        >
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, color: 'var(--fg-1)' }}>
            {plan.name}
          </span>
          {planCurrency && planPricing && (
            <PricingLabel currency={planCurrency} pricing={planPricing} variant="detail" />
          )}
        </div>
      )}

      <ul style={{ margin: 'var(--sp-3) 0 0', paddingLeft: 'var(--sp-5)' }}>
        {DISCLOSED_BASIS.map((line) => (
          <li key={line} style={{ marginBottom: 'var(--sp-1)' }}>
            {line}
          </li>
        ))}
      </ul>

      {costLines.length > 0 && (
        <ul style={{ margin: 'var(--sp-3) 0 0', paddingLeft: 'var(--sp-5)' }}>
          {costLines.map((l) => (
            <li key={l.label} style={{ marginBottom: 'var(--sp-1)' }}>
              <strong>{l.label}</strong> — {l.basis}
            </li>
          ))}
        </ul>
      )}

      <p style={{ margin: 'var(--sp-3) 0 0', color: 'var(--fg-1)' }}>
        <strong>{BILLING_NOT_ENABLED}</strong>
      </p>

      <p style={{ margin: 'var(--sp-2) 0 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-meta)', color: 'var(--fg-4)' }}>
        {DISCLOSURE_VERSION}
      </p>
    </section>
  );
}
